<template>
  <div>
    <AM3InputSelect
      ref="selectRef"
      class="am3-input-country"
      :label="label"
      :model-value="modelValue"
      :options="countryList"
      :option-label="valueType === 'code' ? 'code' : 'name'"
      :option-value="valueType === 'code' ? 'code' : 'iso'"
      :placeholder="placeholder"
      :disabled="disabled"
      :data-test-id="generateDataTestId(dataTestId, 'country-select')"
      :show-selection-indicators="false"
      :clearable="false"
      @update:model-value="$emit('update:modelValue', $event)"
      @change="handleSelect"
      @blur="handleBlur"
    >
      <!-- Add selected flag in left slot -->
      <template
        v-if="selectedCountry && selectedCountry.iso"
        #left
      >
        <AM3FlagIcon
          :flag="selectedCountry.iso"
          size="sm"
          class="ml-2 mt-1"
        />
      </template>
      <template #label>
        <span v-if="required">*</span>
        <slot
          v-else
          name="label"
        />
      </template>
      <template #option="{ option }">
        <div class="flex items-center gap-2">
          <AM3FlagIcon
            v-if="option && option.iso"
            :flag="option.iso"
            size="sm"
          />
          <span>{{ valueType === 'code' ? option?.code : option?.name }}</span>
        </div>
      </template>
    </AM3InputSelect>
    <AM3InputError :error="validationState" />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, watch, ref, onMounted } from 'vue'
import type { PropType } from 'vue'
import AM3InputSelect from '@/components/am3-generic/inputs/entry/AM3InputSelect.vue'
import { countryList } from '@/utils/countries.js'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import AM3FlagIcon from '@/components/am3-generic/base/icon/AM3FlagIcon.vue'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'

export default defineComponent({
  name: 'AM3InputCountry',

  components: {
    AM3InputSelect,
    AM3FlagIcon,
    AM3InputError,
  },

  props: {
    modelValue: {
      type: [String, Object],
      default: null,
    },
    label: {
      type: String,
      default: 'Country',
    },
    placeholder: {
      type: String,
      default: 'Select country...',
    },
    valueType: {
      type: String as PropType<'code' | 'iso'>,
      default: 'code',
      validator: (val: string) => ['code', 'iso'].includes(val),
    },
    displayType: {
      type: String as PropType<'code' | 'name'>,
      default: '',
      validator: (val: string) => ['code', 'name', ''].includes(val),
    },
    optionValue: {
      type: String as PropType<'code' | 'iso' | 'name' | null>,
      default: null,
      validator: (val: string | null) => ['code', 'iso', 'name', null].includes(val),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    dataTestId: {
      type: String,
      default: 'country',
    },

    validate: {
      type: Function,
      default: () => true,
    },

    validateOnBlur: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'select', 'blur'],

  setup(props, { emit }) {
    const selectedCountryRef = ref(null)

    // Setup form validation
    const {
      validationState,
      runValidation: _runValidation,
      resetValidation,
      validateOnBlur,
      handleBlur: _handleBlur,
    } = useFormValidation(props.validate, props.modelValue, props.required)

    // Handle blur with custom logic
    const handleBlur = () => {
      _handleBlur(props.modelValue)
      emit('blur')
    }

    // Public method to run validation
    const runValidation = () => {
      if (props.required && !props.modelValue) {
        validationState.value = 'This is a required field'
      }
      else {
        validationState.value = ''
      }
      return _runValidation(props.modelValue, props.required)
    }

    const selectedCountry = computed(() => {
      // Handle object model value
      if (typeof props.modelValue === 'object' && props.modelValue !== null) {
        return props.modelValue
      }

      // If we have a reference set, use that
      if (selectedCountryRef.value) {
        return selectedCountryRef.value
      }

      // Handle string model value
      if (typeof props.modelValue === 'string' && props.modelValue) {
        const foundCountry = countryList.find(country =>
          props.valueType === 'code'
            ? country.code === props.modelValue
            : country.iso === props.modelValue,
        )

        if (foundCountry) {
          return foundCountry
        }
      }

      // Fallback to the first country in the list if no match was found
      return null
    })

    watch(() => props.modelValue, (newValue) => {
      if (!newValue) {
        selectedCountryRef.value = null
        return
      }

      if (typeof newValue === 'object') {
        selectedCountryRef.value = newValue
        return
      }

      const found = countryList.find(country =>
        props.valueType === 'code'
          ? country.code === newValue
          : country.iso === newValue,
      )

      if (found) {
        selectedCountryRef.value = found
      }
      else {
        console.warn(`Country not found for value: ${newValue}`)
        selectedCountryRef.value = null
      }
    }, { immediate: true })

    // Initialize with default country if none is selected
    onMounted(() => {
      if (!selectedCountryRef.value && countryList.length > 0) {
        // Don't auto-select to avoid unexpected behavior
      }
    })

    const handleSelect = (country: any) => {
      if (!country) {
        console.warn('No country selected in handleSelect')
        return
      }

      selectedCountryRef.value = country

      try {
        if (props.optionValue && typeof country === 'object') {
          emit('update:modelValue', country[props.optionValue] || null)
        }
        else {
          emit('update:modelValue', country)
        }

        emit('select', country)
      }
      catch (error) {
        console.error('Error in handleSelect:', error)
      }
    }

    return {
      countryList,
      generateDataTestId,
      selectedCountry,
      handleSelect,
      validationState,
      handleBlur,
      runValidation,
      resetValidation,
    }
  },
})
</script>
